"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { ShieldCheck, Info, Loader, CircleCheck, CircleAlert } from "lucide-react";
function PrequalificationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="111738" className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
          <CircleCheck className="size-8 text-primary" />
        </div>
        <h3 className="text-2xl font-sans font-bold text-foreground">Inquiry Received</h3>
        <p className="text-muted-foreground max-w-md mx-auto">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-6 rounded-full border-primary text-primary hover:bg-primary/5">
          Submit Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="full_name" rules={{
        required: "Full Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Jane Doe" className="bg-background focus-visible:ring-ring" {...field} />
              </FormControl>
              <FormDescription className="text-xs">Enter your legal name for verification.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email Address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane@lanaconnect.com" className="bg-background focus-visible:ring-ring" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone Number is required",
        pattern: {
          value: /^\+?[0-9\s\-\(\)]+$/,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(555) 000-0000" className="bg-background focus-visible:ring-ring" {...field} />
              </FormControl>
              <FormDescription className="text-xs">We'll use this to coordinate your financing connection.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="zip_code" rules={{
        required: "ZIP Code is required",
        pattern: {
          value: /^[0-9]{5}$/,
          message: "Please enter a valid 5-digit ZIP code"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">ZIP Code</FormLabel>
              <FormControl>
                <Input placeholder="10001" className="bg-background focus-visible:ring-ring" {...field} />
              </FormControl>
              <FormDescription className="text-xs">Enter the ZIP code where the project is located.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="monthly_income" rules={{
        required: "Monthly Gross Income is required",
        min: {
          value: 500,
          message: "Minimum income must be at least $500"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">Monthly Gross Income ($)</FormLabel>
              <FormControl>
                <Input type="number" placeholder="5000" min="500" className="bg-background focus-visible:ring-ring" {...field} />
              </FormControl>
              <FormDescription className="text-xs">Total household income before taxes and deductions.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="credit_score_range" rules={{
        required: "Estimated Credit Score is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">Estimated Credit Score</FormLabel>
              <FormControl>
                <select className="flex h-10 w-full min-w-0 rounded-md border border-input bg-background px-3 py-2 text-base shadow-sm transition-all outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 md:text-sm dark:bg-input/30 aria-invalid:ring-destructive/40 aria-invalid:border-destructive" {...field}>
                  <option value="" disabled className="bg-background text-muted-foreground">Select your credit range</option>
                  <option value="Excellent (720-850)" className="bg-background text-foreground">Excellent (720-850)</option>
                  <option value="Good (660-719)" className="bg-background text-foreground">Good (660-719)</option>
                  <option value="Fair (600-659)" className="bg-background text-foreground">Fair (600-659)</option>
                  <option value="Poor (Below 600)" className="bg-background text-foreground">Poor (Below 600)</option>
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="desired_loan_amount" rules={{
        required: "Desired Financing Amount is required",
        min: {
          value: 1000,
          message: "Minimum amount is $1,000"
        },
        max: {
          value: 50000,
          message: "Maximum amount is $50,000"
        }
      }} render={({
        field
      }) => <FormItem className="md:col-span-2">
              <FormLabel className="text-foreground font-medium">Desired Financing Amount ($)</FormLabel>
              <FormControl>
                <Input type="number" placeholder="25000" min="1000" max="50000" className="bg-background focus-visible:ring-ring" {...field} />
              </FormControl>
              <FormDescription className="text-xs">Enter the amount you wish to finance for your upcoming project ($1,000 - $50,000).</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <Alert variant="destructive" className="rounded-xl">
          <CircleAlert className="size-4" />
          <AlertTitle>Connection Error</AlertTitle>
          <AlertDescription>{submitError}</AlertDescription>
        </Alert>}

      <div className="pt-4 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4">
        <p className="text-xs text-muted-foreground flex items-center gap-2">
          <ShieldCheck className="size-4 text-primary" />
          Your data is encrypted and secure with Lana Connect LLC.
        </p>
        <Button type="submit" size="lg" disabled={isSubmitting} className="w-full sm:w-auto min-w-[200px] rounded-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-md transition-all hover:scale-[1.02]">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Connecting...
            </> : 'Find My Connection'}
        </Button>
      </div>
    </Form>;
}
export default function PrequalifyForFinancing() {
  return <section id="inquiry-portal" className="bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-primary/5 rounded-full blur-[120px] opacity-40" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-ring/10 rounded-full blur-[100px] opacity-20" />
      </div>

      <div className="container mx-auto px-4 relative z-10 max-w-4xl">
        <div className="text-center mb-12 space-y-4">
          <div className="inline-flex items-center justify-center p-3 bg-primary/5 rounded-full mb-4 border border-primary/10">
            <ShieldCheck className="size-8 text-primary" />
          </div>
          <h2 className="text-4xl md:text-5xl font-sans font-extrabold tracking-tight">
            Experts, <span className="font-serif italic font-medium text-primary">One Connection</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto font-sans">
            Lana Connect LLC bridges the gap between your home improvement goals and the ideal financing platform. We simplify the path to project funding.
          </p>
        </div>

        <div className="bg-card border border-border rounded-3xl shadow-xl overflow-hidden">
          <div className="p-6 md:p-8 border-b border-border bg-muted/20 backdrop-blur-sm">
            <Alert className="bg-background/80 border-ring/20 text-foreground rounded-2xl">
              <Info className="size-4 text-ring" />
              <AlertTitle className="text-primary font-bold">Secure Connection Guarantee</AlertTitle>
              <AlertDescription className="text-muted-foreground">
                Lana Connect LLC is not a direct lender. We use a "soft pull" to identify your best financing matches, ensuring there is no impact on your credit score during this discovery phase.
              </AlertDescription>
            </Alert>
          </div>

          <div className="p-6 md:p-8">
            <FormProvider formKey="inquiry_portal_contact" formId="inquiry_portal_contact" sectionName="PrequalifyForFinancing" defaultValues={{
            full_name: "",
            email: "",
            phone: "",
            zip_code: "",
            monthly_income: "",
            credit_score_range: "",
            desired_loan_amount: ""
          }} validationRules={{
            full_name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true,
              pattern: "phone"
            },
            zip_code: {
              required: true,
              pattern: "^[0-9]{5}$"
            },
            monthly_income: {
              required: true,
              min: 500
            },
            credit_score_range: {
              required: true
            },
            desired_loan_amount: {
              required: true,
              min: 1000,
              max: 50000
            }
          }} submitText="Find My Connection" successMessage="Thank you! Lana Connect LLC has received your details. We are matching you with the most suitable financing platforms for your specific needs. An expert will reach out shortly.">
              <PrequalificationForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}